import {
  AlertTriangle,
  ArrowDown,
  ArrowLeftRight,
  ArrowUp,
  File,
  Settings,
  ShoppingCart,
  TruckIcon,
  Warehouse,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@Utils';

import {
  LabelSection,
  Section,
  Separator,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@Components';
import { ConfigInventory } from '@Models';

interface InventorySectionProps {
  value: ConfigInventory;
  onChange: (value: ConfigInventory) => void;
  className?: string;
}

const InventorySection = ({ value, onChange, className }: InventorySectionProps) => {
  const { t } = useTranslation();

  const handleOptionToggle = (option: string) => {
    const newOptions = value.Options.includes(option)
      ? value.Options.filter((o) => o !== option)
      : [...value.Options, option];

    onChange({ ...value, Options: newOptions });
  };

  // Define inventory impact for each option
  type InventoryImpact = 'increase' | 'decrease' | 'both' | 'none';

  const optionItems = [
    {
      id: 'OnOrder',
      label: t('ConfigurationPolicy.Inventory.OnOrder'),
      icon: <File size={20} />,
      impact: 'increase' as InventoryImpact,
      tooltip: t('ConfigurationPolicy.Inventory.OnOrderTooltip'),
    },
    {
      id: 'InTransit',
      label: t('ConfigurationPolicy.Inventory.InTransit'),
      icon: <TruckIcon size={20} />,
      impact: 'both' as InventoryImpact,
      tooltip: t('ConfigurationPolicy.Inventory.InTransitTooltip'),
    },
    {
      id: 'InWarehouse',
      label: t('ConfigurationPolicy.Inventory.InWarehouse'),
      icon: <Warehouse size={20} />,
      impact: 'increase' as InventoryImpact,
      tooltip: t('ConfigurationPolicy.Inventory.InWarehouseTooltip'),
    },
    {
      id: 'Allocated',
      label: t('ConfigurationPolicy.Inventory.Allocated'),
      icon: <ShoppingCart size={20} />,
      impact: 'decrease' as InventoryImpact,
      tooltip: t('ConfigurationPolicy.Inventory.AllocatedTooltip'),
    },
    {
      id: 'Aging',
      label: t('ConfigurationPolicy.Inventory.Aging'),
      icon: <AlertTriangle size={20} />,
      impact: 'decrease' as InventoryImpact,
      tooltip: t('ConfigurationPolicy.Inventory.AgingTooltip'),
    },
    {
      id: 'Custom',
      label: t('ConfigurationPolicy.Inventory.Custom'),
      icon: <Settings size={20} />,
      impact: 'none' as InventoryImpact,
      tooltip: t('ConfigurationPolicy.Inventory.CustomTooltip'),
    },
  ];

  // Get the appropriate impact icon based on the impact type
  const getImpactIcon = (impact: InventoryImpact) => {
    switch (impact) {
      case 'increase':
        return <ArrowUp size={16} className="text-green-500" />;
      case 'decrease':
        return <ArrowDown size={16} className="text-red-500" />;
      case 'both':
        return <ArrowLeftRight size={16} className="text-amber-500" />;
      case 'none':
      default:
        return null;
    }
  };

  // Get tooltip text for impact
  const getImpactTooltip = (impact: InventoryImpact) => {
    switch (impact) {
      case 'increase':
        return t('ConfigurationPolicy.Inventory.ImpactIncrease');
      case 'decrease':
        return t('ConfigurationPolicy.Inventory.ImpactDecrease');
      case 'both':
        return t('ConfigurationPolicy.Inventory.ImpactBoth');
      case 'none':
      default:
        return '';
    }
  };

  return (
    <TooltipProvider>
      <div className={cn('space-y-6 mt-10', className)}>
        <Section
          left={
            <LabelSection
              label={t('ConfigurationPolicy.Inventory.InventoryOnHand')}
              description={t('ConfigurationPolicy.Inventory.InventoryOnHandDesc')}
            />
          }
          right={
            <div className="flex">
              <div className="flex p-4 ml-auto">
                <Switch id="inventory-on-hand" checked className="cursor-default" />
              </div>
            </div>
          }
        />

        <Separator orientation="horizontal" className="w-full bg-gray-300 h-[1px]" />

        <Section
          left={
            <LabelSection
              label={t('ConfigurationPolicy.Inventory.AdditionalInventoryOptions')}
              description={t('ConfigurationPolicy.Inventory.AdditionalInventoryOptionsDesc')}
            />
          }
          right={
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {optionItems.map((option) => (
                <Tooltip key={option.id}>
                  <TooltipTrigger asChild>
                    <div
                      className={cn(
                        'p-3 border rounded-lg cursor-default transition-all',
                        value.Options.includes(option.id)
                          ? 'border-brand-300 bg-primary/5 border-solid'
                          : 'border-gray-300 hover:border-gray-700 border-solid',
                      )}
                      onClick={() => handleOptionToggle(option.id)}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div
                            className={cn(
                              'rounded-full',
                              value.Options.includes(option.id) ? 'text-brand-500' : 'text-gray-700',
                            )}>
                            {option.icon}
                          </div>
                          <span className="font-medium text-sm">{option.label}</span>
                        </div>

                        {option.impact !== 'none' && (
                          <Tooltip key={option.id + option.impact}>
                            <TooltipTrigger className="border-0 bg-transparent">
                              <div className="ml-2">{getImpactIcon(option.impact)}</div>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <p>{getImpactTooltip(option.impact)}</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p>{option.tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          }
        />
      </div>
    </TooltipProvider>
  );
};

export default InventorySection;
